import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaNetworkWired,
  FaCloud,
  FaShieldAlt,
  FaTools,
  FaDatabase,
  FaRedo,
  FaServer,
  FaKeyboard,
  FaDesktop,
  FaCogs,
  FaBrain,
  FaBalanceScale,
  FaLock,
  FaPlug,
  FaSnowflake,
  FaUserShield,
  FaHeartbeat,
  FaHdd,
  FaSave,
  FaCreditCard,
  FaUserMd,
  FaProjectDiagram,
  FaCode,
  FaGlobe,
  FaMobileAlt,
  FaPuzzlePiece,
  FaExpandArrowsAlt,
  FaPhoneAlt,
  FaUser,
  FaMapMarkerAlt,
  FaTasks,
  FaCheckCircle,
  FaAward,
  FaGem,
  FaThumbsUp,
  FaChevronDown,
  FaBolt,
  FaLayerGroup,
  FaHeadset,
  FaArrowRight,
} from "react-icons/fa";
import Eyebrow from "../common/Eyebrow";
import CountUp from "../common/CountUp";
import TiltCard from "../DataCenters/TiltCard";
import ProcessTimeline from "./ProcessTimeline";
import CertTrustStrip from "../DataCenters/CertTrustStrip";
import BrandPartners from "../company/BrandPartners/BrandPartners";
import CustomerStory from "../company/Testimonials";
import { ALL_SERVICES } from "../../constant/HeaderData";
import { ALL_DATA_CENTERS } from "../../constant/AllDataCenters";

const WHY_CHOOSE_ICONS = [FaCheckCircle, FaAward, FaGem, FaThumbsUp];

const ICON_COMPONENTS = {
  FaTasks,
  FaMapMarkerAlt,
  FaUser,
  FaNetworkWired,
  FaCloud,
  FaShieldAlt,
  FaTools,
  FaDatabase,
  FaRedo,
  FaServer,
  FaKeyboard,
  FaDesktop,
  FaCogs,
  FaBrain,
  FaBalanceScale,
  FaLock,
  FaPlug,
  FaSnowflake,
  FaUserShield,
  FaHeartbeat,
  FaHdd,
  FaSave,
  FaCreditCard,
  FaUserMd,
  FaProjectDiagram,
  FaCode,
  FaGlobe,
  FaMobileAlt,
  FaPuzzlePiece,
  FaExpandArrowsAlt,
  FaPhoneAlt,
};

const ICONS = Object.fromEntries(
  Object.entries(ICON_COMPONENTS).map(([key, Icon]) => [
    key,
    <Icon
      size={40}
      className="text-[var(--website-secondary-color)] mx-auto"
    />,
  ])
);

const TRUST_STATS = [
  {
    icon: FaMapMarkerAlt,
    value: Object.keys(ALL_DATA_CENTERS).length,
    suffix: "+",
    label: "Locations, India & international",
  },
  {
    icon: FaBolt,
    value: 99.99,
    decimals: 2,
    suffix: "%",
    label: "Uptime SLA",
  },
  {
    icon: FaLayerGroup,
    value: null,
    display: "Tier-3",
    label: "Facility design",
  },
  { icon: FaHeadset, value: null, display: "24/7", label: "NOC & support" },
];

const TrustStat = ({ stat }) => {
  const Icon = stat.icon;
  return (
    <TiltCard className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 text-left backdrop-blur-sm">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(var(--website-secondary-rgb),0.15)] text-[var(--website-secondary-color)]">
        <Icon size={16} />
      </div>
      <p className="mt-4 text-2xl font-bold text-white md:text-3xl">
        {stat.value !== null ? (
          <CountUp
            end={stat.value}
            decimals={stat.decimals || 0}
            suffix={stat.suffix}
          />
        ) : (
          stat.display
        )}
      </p>
      <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-400">
        {stat.label}
      </p>
    </TiltCard>
  );
};

const Services = ({
  whyChooseUs,
  serviceSection,
  howItWorks,
  serviceData,
  pageId,
}) => {
  const [openFaq, setOpenFaq] = useState(0);

  const {
    title = "Default Title",
    description = "Default description",
    serviceTitle = "Default service Title",
    serviceSubTitle = "Default service Sub Title",
    detailSectionTitle = "Default detail Section Title",
    detailSectionSubTitle = "Default detail Section Sub Title",
  } = serviceData || {};

  const relatedServices = ALL_SERVICES.filter(
    (service) => service.pageId !== pageId
  ).slice(0, 3);

  const DATA_CENTER_PAGE_IDS = [
    "colocation_services",
    "dedicated_servers",
    "vps_services",
    "rental_hardware",
    "cloud_services",
  ];

  const jsonLdLocalBusiness = () => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      additionalType: DATA_CENTER_PAGE_IDS.includes(pageId)
        ? ["https://schema.org/DataCenter", "https://schema.org/ITServices"]
        : ["https://schema.org/ITServices"],
      name: "QuantaRock Systems",
      url: `https://quantarock.in/services/${pageId}`,
      description: description,
      telephone: "+91-9755010858",
      email: "info@quantarock.in",
      address: {
        "@type": "PostalAddress",
        streetAddress: "444, Shyam Nagar(main)",
        addressLocality: "Sukhliya Indore",
        addressRegion: "Madhya Pradesh",
        postalCode: "452010",
        addressCountry: "India",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: "22.719569",
        longitude: "75.857727",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Indore",
          containedInPlace: {
            "@type": "State",
            name: "Madhya Pradesh",
            containedInPlace: { "@type": "Country", name: "India" },
          },
        },
        "Indore",
        "Madhya Pradesh",
        "India",
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+91-9755010858",
          contactType: "customer service",
          areaServed: "IN",
        },
      ],
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "09:00",
        closes: "18:00",
      },
      sameAs: [
        "https://www.linkedin.com/company/quantarock-systems",
        "https://x.com/QuantaRock",
        "https://www.instagram.com/quantarock_systems_india/",
        "https://www.youtube.com/@Quantarock",
        "https://www.facebook.com/profile.php?id=61550422080802",
      ],
      priceRange: "$$",
      businessType: ["DataCenter", "HostingProvider", "ITServices"],
    };
    return JSON.stringify(ld);
  };

  const jsonLdFAQ = () => {
    if (!serviceData?.faq) return null;
    const mainEntity = serviceData.faq.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    }));
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    });
  };

  const jsonLdBreadcrumb = () => {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://quantarock.in",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://quantarock.in/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: title,
          item: `https://quantarock.in/services/${pageId}`,
        },
      ],
    });
  };

  return (
    <div className="bg-white">
      <Helmet>
        <title>
          {pageId === "colocation_services"
            ? `Data Center Colocation in Indore | QuantaRock`
            : `${title} | QuantaRock`}
        </title>
        <meta
          name="description"
          content={
            pageId === "colocation_services"
              ? "QuantaRock provides secure, compliant data center colocation in Indore with reliable power, cooling and multi-carrier connectivity. Serving customers across Indore and India."
              : description
          }
        />
        {pageId === "colocation_services" && (
          <meta
            name="keywords"
            content="colocation Indore, data center colocation, rack space Indore, managed colocation India, secure hosting Indore, colocation services"
          />
        )}
        {pageId === "colocation_services" && (
          <meta name="geo.position" content="22.719569;75.857727" />
        )}
        {pageId === "colocation_services" && (
          <meta name="ICBM" content="22.719569,75.857727" />
        )}
        {pageId === "colocation_services" && (
          <meta name="geo.placename" content="Indore" />
        )}
        {pageId === "colocation_services" && (
          <meta name="geo.region" content="IN-MP" />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta
          property="og:title"
          content={
            pageId === "colocation_services"
              ? "Data Center Colocation in Indore | QuantaRock"
              : `${title} | QuantaRock`
          }
        />
        <meta
          property="og:description"
          content={
            pageId === "colocation_services"
              ? "Secure, scalable colocation and rack space in Indore. Multi-carrier connectivity, 24/7 monitoring, and expert support for your data center needs."
              : description
          }
        />
        <meta
          property="og:url"
          content={`https://quantarock.in/services/${pageId}`}
        />
        <meta
          property="og:image"
          content="https://quantarock.in/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            pageId === "colocation_services"
              ? "Data Center Colocation in Indore | QuantaRock"
              : `${title} | QuantaRock`
          }
        />
        <meta
          name="twitter:description"
          content={
            pageId === "colocation_services"
              ? "Secure, scalable colocation services in Indore with reliable power, cooling, and multi-carrier connectivity."
              : description
          }
        />
        <meta name="robots" content="index,follow" />
        <link
          rel="canonical"
          href={`https://quantarock.in/services/${pageId}`}
        />
      </Helmet>
      <script type="application/ld+json">{jsonLdLocalBusiness()}</script>
      {serviceData?.faq && (
        <script type="application/ld+json">{jsonLdFAQ()}</script>
      )}
      <script type="application/ld+json">{jsonLdBreadcrumb()}</script>

      {/* Trust stat bar */}
      <section className="relative overflow-hidden bg-[var(--surface-dark)] px-6 py-16">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
        <div className="relative mx-auto grid max-w-6xl grid-cols-2 gap-4 [perspective:800px] md:grid-cols-4">
          {TRUST_STATS.map((stat) => (
            <TrustStat key={stat.label} stat={stat} />
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>Why QuantaRock</Eyebrow>
          </div>
          <h2 className="mx-auto mt-3 max-w-4xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
            Why Choose QuantaRock Systems for {title}?
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-center leading-relaxed text-slate-600">
            {description}
          </p>
          <div className="mt-10 grid gap-6 [perspective:800px] md:grid-cols-3">
            {whyChooseUs.map((item, index) => {
              const Icon = WHY_CHOOSE_ICONS[index % WHY_CHOOSE_ICONS.length];
              return (
                <TiltCard
                  key={index}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--website-primary-color)] text-[var(--website-secondary-color)] transition group-hover:bg-[var(--website-secondary-color)] group-hover:text-[var(--website-primary-color)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-[var(--website-primary-color)]">
                    {item.title}
                  </h3>
                  <p className="mt-2 leading-relaxed text-slate-600">
                    {item.description}
                  </p>
                </TiltCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications trust strip */}
      <CertTrustStrip />

      {/* FAQ Section */}
      {serviceData?.faq && (
        <section className="bg-[var(--surface-dark)] px-6 py-20">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-[var(--website-secondary-color)]">
            FAQ
          </p>
          <h2 className="mt-3 text-center text-3xl font-bold tracking-tight text-white md:text-4xl">
            Frequently Asked Questions
          </h2>
          <div className="mx-auto mt-10 max-w-4xl space-y-4">
            {serviceData.faq.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className={`overflow-hidden rounded-xl border transition-colors duration-300 ${
                    isOpen
                      ? "border-[rgba(var(--website-secondary-rgb),0.6)] bg-white/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={`svc-faq-answer-${pageId}-${i}`}
                    className="flex w-full items-start gap-4 p-6 text-left"
                  >
                    <span
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-300 ${
                        isOpen
                          ? "bg-[var(--website-secondary-color)] text-[var(--website-primary-color)]"
                          : "bg-white/10 text-[var(--website-secondary-color)]"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="flex-1 pt-1 text-lg font-semibold text-white">
                      {f.question}
                    </h3>
                    <span
                      className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                        isOpen
                          ? "rotate-180 bg-[var(--website-secondary-color)] text-[var(--website-primary-color)]"
                          : "bg-white/10 text-slate-300"
                      }`}
                    >
                      <FaChevronDown size={12} />
                    </span>
                  </button>
                  <div
                    id={`svc-faq-answer-${pageId}-${i}`}
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="ml-[4.25rem] mr-6 mb-6 border-l-2 border-[rgba(var(--website-secondary-rgb),0.5)] pl-4 leading-relaxed text-slate-300">
                        {f.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* What we offer Section */}
      <section className="bg-white px-6 py-20">
        <div className="text-center">
          <Eyebrow>What we offer</Eyebrow>
        </div>
        <h2 className="mt-3 text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
          {serviceTitle}
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-center leading-relaxed text-slate-600">
          {serviceSubTitle}
        </p>
        <div className="mx-auto mt-10 grid max-w-6xl gap-6 [perspective:800px] md:grid-cols-3">
          {serviceSection.map((service, index) => (
            <TiltCard
              key={index}
              className="flex flex-col items-center rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
            >
              <div>{ICONS[service.iconKey]}</div>
              <h3 className="mt-5 text-xl font-semibold text-[var(--website-primary-color)]">
                {service.title}
              </h3>
              <p className="mt-2 leading-relaxed text-slate-600">
                {service.description}
              </p>
            </TiltCard>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="text-center">
          <Eyebrow>Process</Eyebrow>
        </div>
        <h2 className="mt-3 text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
          {detailSectionTitle}
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-center leading-relaxed text-slate-600">
          {detailSectionSubTitle}
        </p>
        <div className="mx-auto max-w-6xl">
          <ProcessTimeline steps={howItWorks} />
        </div>
      </section>

      {/* Brands & Partners */}
      <BrandPartners />

      {/* Customer testimonials */}
      <CustomerStory />

      {/* Related services */}
      {relatedServices.length > 0 && (
        <section className="bg-slate-50 px-6 py-20">
          <div className="mx-auto max-w-6xl">
            <div className="text-center">
              <Eyebrow>Explore more</Eyebrow>
            </div>
            <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
              Related services
            </h2>
            <div className="mt-10 grid gap-6 [perspective:800px] sm:grid-cols-3">
              {relatedServices.map((service) => (
                <TiltCard
                  key={service.pageId}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                >
                  <Link
                    to={`/services/${service.pageId}`}
                    className="flex h-full flex-col"
                  >
                    <img
                      src={
                        service.logoURL
                          ? require(`../../assets/HeaderIcons/${service.logoURL}`)
                          : require(`../../assets/HeaderIcons/consultation.png`)
                      }
                      alt={service.title}
                      loading="lazy"
                      className="h-10 w-10 object-contain"
                    />
                    <h3 className="mt-4 text-lg font-semibold text-[var(--website-primary-color)]">
                      {service.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                      {service.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--website-primary-color)] transition group-hover:gap-3 group-hover:text-[var(--website-secondary-color)]">
                      Explore
                      <FaArrowRight size={12} />
                    </span>
                  </Link>
                </TiltCard>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[var(--surface-dark)] px-6 py-20 text-center">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--website-secondary-color)]">
            Plan with confidence
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">
            Ready to build a better {title.toLowerCase()} strategy?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-300">
            Tell us about your requirements and our infrastructure specialists
            will help you choose the right path.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex rounded-full bg-[var(--website-secondary-color)] px-6 py-3 text-sm font-semibold text-[var(--on-secondary)] transition hover:brightness-110"
          >
            Talk to an expert
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
